import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { 
  FaCalendarAlt, FaFileExcel, FaFilePdf, FaFilter, FaChartBar, 
  FaSync, FaTimes, FaCalendar
} from "react-icons/fa";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import "./BillWiseSale.css";
import logo from "../../assets/logo.png";

export default function DateWiseSale() {
  const [saleData, setSaleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().split('T')[0];
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    startDate: startOfDay,
    endDate: endOfDay,
    minAmount: "",
    maxAmount: ""
  });

  const fetchSaleData = async () => {
    try {
      setLoading(true);
      const response = await fetchData("morning_sale", null, "id", {});
      const sales = Array.isArray(response) ? response : response?.data || [];
      setSaleData(sales);
      applyFilters();
    } catch (error) {
      console.error("Error fetching sale data:", error);
      toast.error("Failed to fetch sale data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaleData();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [saleData, filters]);

  const applyFilters = () => {
    let filtered = saleData.filter(sale => {
      let saleDate = null;
      if (sale.dte) {
        try {
          const dateObj = new Date(sale.dte);
          if (!isNaN(dateObj.getTime())) {
            saleDate = dateObj.toISOString().split('T')[0];
          }
        } catch (e) {
          saleDate = null;
        }
      }
      
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;
      const saleDateObj = saleDate ? new Date(saleDate) : null;

      const dateMatch = 
        (!startDate || !saleDateObj || saleDateObj >= startDate) &&
        (!endDate || !saleDateObj || saleDateObj <= endDate);

      const amountMatch = 
        (!filters.minAmount || parseFloat(sale.total_sale || 0) >= parseFloat(filters.minAmount)) &&
        (!filters.maxAmount || parseFloat(sale.total_sale || 0) <= parseFloat(filters.maxAmount));

      return dateMatch && amountMatch;
    });

    setFilteredData(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: startOfDay,
      endDate: endOfDay,
      minAmount: "",
      maxAmount: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const calculateTotals = () => {
    return {
      totalRecords: filteredData.length,
      cash: filteredData.reduce((sum, sale) => sum + (parseFloat(sale.cash) || 0), 0),
      card: filteredData.reduce((sum, sale) => sum + (parseFloat(sale.card) || 0), 0),
      transfer: filteredData.reduce((sum, sale) => sum + (parseFloat(sale.transfer) || 0), 0),
      discount: filteredData.reduce((sum, sale) => sum + (parseFloat(sale.discount) || 0), 0),
      totalAmount: filteredData.reduce((sum, sale) => sum + (parseFloat(sale.total_sale) || 0), 0)
    };
  };

  const totals = calculateTotals();

  const exportToCSV = () => {
    const csvData = filteredData.map(sale => ({
      'Date': formatDate(sale.dte),
      'Cash': sale.cash || 0,
      'Card': sale.card || 0,
      'Transfer': sale.transfer || 0,
      'Discount': sale.discount || 0,
      'Total Sale': sale.total_sale || 0
    }));

    return csvData;
  };

  const exportToPDF = () => {
    const doc = new jsPDF('portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 8;

    // Compact Header with light blue gradient
    doc.setFillColor(230, 240, 255);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Border for header
    doc.setDrawColor(180, 200, 230);
    doc.setLineWidth(0.5);
    doc.rect(0, 0, pageWidth, 25, 'S');
    
    // Add logo
    const imgWidth = 35;
    const imgHeight = 15;
    doc.addImage(logo, 'PNG', 14, 5, imgWidth, imgHeight);
    
    // Report Title
    doc.setTextColor(40, 80, 150);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('DATE WISE SALE REPORT', pageWidth / 2, 11, { align: 'center' });
    
    // Date Range and Generated timestamp
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, 'normal');
    const dateRange = `Period: ${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
    doc.text(dateRange, pageWidth / 2, 18, { align: 'center' });
    
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth / 2, 22, { align: 'center' });
    
    currentY = 30;

    // Summary Statistics Box - 3 Column Layout
    doc.setFillColor(250, 252, 255);
    doc.rect(10, currentY, pageWidth - 20, 25, 'F');
    doc.setDrawColor(200, 210, 230);
    doc.setLineWidth(0.3);
    doc.rect(10, currentY, pageWidth - 20, 25, 'S');
    
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    
    // Three column layout
    const col1X = 15;
    const col2X = 75;
    const col3X = 135;
    
    // Row 1
    doc.text(`Total Records:`, col1X, currentY + 7);
    doc.setFont(undefined, 'normal');
    doc.text(`${totals.totalRecords}`, col1X + 28, currentY + 7);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Cash:`, col2X, currentY + 7);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.cash.toFixed(2)}`, col2X + 15, currentY + 7);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Card:`, col3X, currentY + 7);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.card.toFixed(2)}`, col3X + 15, currentY + 7);
    
    // Row 2
    doc.setFont(undefined, 'bold');
    doc.text(`Transfer:`, col1X, currentY + 14);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.transfer.toFixed(2)}`, col1X + 28, currentY + 14);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Discount:`, col2X, currentY + 14);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.discount.toFixed(2)}`, col2X + 15, currentY + 14);
    
    doc.setFont(undefined, 'bold');
    doc.setTextColor(40, 80, 150);
    doc.text(`Total Sale:`, col3X, currentY + 14);
    doc.setFontSize(9);
    doc.text(`THB ${totals.totalAmount.toFixed(2)}`, col3X + 25, currentY + 14);
    
    currentY += 31;

    // Table
    const tableColumn = ["#", "Date", "Cash", "Card", "Transfer", "Discount", "Total Sale"];
    const tableRows = filteredData.map((sale, index) => [
      (index + 1).toString(),
      formatDate(sale.dte),
      (sale.cash?.toFixed(0) || '0'),
      (sale.card?.toFixed(0) || '0'),
      (sale.transfer?.toFixed(0) || '0'),
      (sale.discount?.toFixed(0) || '0'),
      (sale.total_sale?.toFixed(0) || '0')
    ]);

    // Add totals row
    tableRows.push([
      '',
      'TOTAL',
      totals.cash.toFixed(0),
      totals.card.toFixed(0),
      totals.transfer.toFixed(0),
      totals.discount.toFixed(0),
      totals.totalAmount.toFixed(0)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY,
      theme: "grid",
      headStyles: {
        fillColor: [230, 240, 255],
        textColor: [40, 80, 150],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [180, 200, 230]
      },
      styles: {
        fontSize: 7,
        cellPadding: { top: 1.5, right: 1, bottom: 1.5, left: 1 },
        lineColor: [220, 230, 245],
        lineWidth: 0.1,
        textColor: [60, 60, 60]
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12, fontStyle: 'normal' },
        1: { halign: 'left', cellWidth: 30 },
        2: { halign: 'left', cellWidth: 24 },
        3: { halign: 'left', cellWidth: 24 },
        4: { halign: 'left', cellWidth: 24 },
        5: { halign: 'left', cellWidth: 24 },
        6: { halign: 'left', cellWidth: 28, fontStyle: 'bold', textColor: [40, 80, 150] }
      },
      didParseCell: function(data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [230, 240, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [40, 80, 150];
        }
      },
      margin: { left: 10, right: 10 },
      tableWidth: pageWidth - 20
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY || currentY;
    if (finalY < pageHeight - 15) {
      doc.setDrawColor(200, 210, 230);
      doc.setLineWidth(0.3);
      doc.line(10, pageHeight - 12, pageWidth - 10, pageHeight - 12);
      
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text('This is a computer-generated report', pageWidth / 2, pageHeight - 8, { align: 'center' });
    }

    doc.save(`date_wise_sale_${format(new Date(), "ddMMyyyy_HHmmss")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <Layout>
      <div className="bill-wise-sale-container">
        <Header title="📅 Date Wise Sale Report" subtitle="Analyze your daily sales performance" />

        {/* Filters Section */}
        <div className="bws-card mb-3">
          <div className="bws-card-header">
            <button 
              className="bws-btn bws-btn-primary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="me-2" /> {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            <button 
              className="bws-btn bws-btn-secondary"
              onClick={clearFilters}
              disabled={loading}
            >
              <FaTimes className="me-2" /> Clear Filters
            </button>
            <button 
              className="bws-btn bws-btn-refresh"
              onClick={fetchSaleData}
              disabled={loading}
            >
              <FaSync className={loading ? 'spinning' : ''} className="me-2" /> Refresh
            </button>
          </div>

          {showFilters && (
            <div className="bws-card-body">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Start Date</label>
                  <input 
                    type="date" 
                    className="bws-input"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>End Date</label>
                  <input 
                    type="date" 
                    className="bws-input"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Min Amount</label>
                  <input 
                    type="number" 
                    className="bws-input"
                    placeholder="Min amount"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Max Amount</label>
                  <input 
                    type="number" 
                    className="bws-input"
                    placeholder="Max amount"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Section */}
        <div className="bws-stats-container">
          <div className="bws-stat-card">
            <div className="stat-label">Total Records</div>
            <div className="stat-value">{totals.totalRecords}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Cash</div>
            <div className="stat-value">{formatCurrency(totals.cash)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Card</div>
            <div className="stat-value">{formatCurrency(totals.card)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Transfer</div>
            <div className="stat-value">{formatCurrency(totals.transfer)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Discount</div>
            <div className="stat-value">{formatCurrency(totals.discount)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Total Sale</div>
            <div className="stat-value">{formatCurrency(totals.totalAmount)}</div>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="bws-card mb-3">
          <div className="bws-card-header">
            <button className="bws-btn bws-btn-success" onClick={exportToPDF}>
              <FaFilePdf className="me-2" /> Export PDF
            </button>
            <CSVLink 
              data={exportToCSV()} 
              filename={`date_wise_sale_${format(new Date(), "ddMMyyyy_HHmmss")}.csv`}
              className="bws-btn bws-btn-info"
            >
              <FaFileExcel className="me-2" /> Export CSV
            </CSVLink>
          </div>
        </div>

        {/* Data Table */}
        <div className="bws-card">
          <div className="bws-card-body-zero">
            {loading ? (
              <div className="bws-loading">Loading sale data...</div>
            ) : filteredData.length === 0 ? (
              <div className="bws-empty-state">
                <FaCalendar className="empty-icon" />
                <p>No sales found for the selected filters</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="bws-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Cash</th>
                        <th>Card</th>
                        <th>Transfer</th>
                        <th>Discount</th>
                        <th>Total Sale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((sale, idx) => {
                        return (
                          <tr key={idx}>
                            <td><strong>{formatDate(sale.dte)}</strong></td>
                            <td className="amount-cell">{formatCurrency(sale.cash)}</td>
                            <td className="amount-cell">{formatCurrency(sale.card)}</td>
                            <td className="amount-cell">{formatCurrency(sale.transfer)}</td>
                            <td className="amount-cell">{formatCurrency(sale.discount)}</td>
                            <td className="amount-cell">{formatCurrency(sale.total_sale)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="footer-label">TOTAL</td>
                        <td className="footer-value">{formatCurrency(totals.cash)}</td>
                        <td className="footer-value">{formatCurrency(totals.card)}</td>
                        <td className="footer-value">{formatCurrency(totals.transfer)}</td>
                        <td className="footer-value">{formatCurrency(totals.discount)}</td>
                        <td className="footer-value">{formatCurrency(totals.totalAmount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bws-pagination">
                  <button 
                    className="bws-btn bws-btn-small"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages} (Showing {currentItems.length} of {filteredData.length})
                  </span>
                  <div className="pagination-select">
                    <label>Records per page:</label>
                    <select 
                      value={itemsPerPage} 
                      onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="bws-pagination-combo"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                  <button 
                    className="bws-btn bws-btn-small"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </Layout>
  );
}
